import React, { useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useHistory, useLocation, useParams, withRouter } from "react-router-dom";

import { AppContext } from "../../providers/Redux/Reducers/AppContext";

import { IonButton, IonCol, IonGrid, IonList, IonLoading, IonRow, IonSpinner } from "@ionic/react";

import "../product-detail/product-detail.scss";
import styles from "../product-detail/product-detail.module.scss";
import classNames from "classnames/bind";
import MaxWidthContainer from "../../components/UI/MaxWidthContainer/MaxWidthContainer";
import ProductDetailInStock from "../../components/ProductDetail/ProductDetailInstock/ProductDetailInStock";
import ProductDetailRating from "../../components/ProductDetail/ProductDetailRating/ProductDetailRating";
import ProductDetailSlideBadges from "../../components/ProductDetail/ProductDetailSlideBadges/ProductDetailSlideBadges";
import ProductDetailCounter from "../../components/ProductDetail/ProductDetailCounter/ProductDetailCounter";
import ProductDetailInfo from "../../components/ProductDetail/ProductDetailInfo/ProductDetailInfo";
import { ProductType, Product as TProduct } from "@core/types";
import useGetProductByIdQuery from "../../providers/products/useGetProductByIdQuery";
import useGetCategoryByIdQuery from "../../providers/categories/useGetCategoryByIdQuery";
import useGetProductsQuery from "../../providers/products/useGetProductsQuery";
import { ProductDescription } from "../../components/UI/ProductDescription/ProductDescription";

import { AddToCartProduct, ProductId, useCartData } from "../../providers/cartDataStore";
import { ProductsSlider } from "../../components/ProductsSlider/ProductsSlider";
import useGetProductVariations from "../../providers/products/useGetProductVariations";
import Page from "../page-layout";
import { useConfig } from "../../providers/config";
import { AxiosError } from "axios";
import { useActiveOrMainShippingZone } from "../../providers/shippingZones";
import { Swiper, SwiperSlide } from "swiper/react";

import { Navigation } from "swiper/modules";
import { SmartImage } from "../../components/SmartImage/SmartImage";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/auth-context";
import FortimeMainPageProduct from "../../4time/src/entities/Product/MainPageProduct";
import { VariationBadgesFortime, AttributeSelector } from "../../4time/src/entities";
import { useProductDetailCounter } from "../../hooks/useProductDetailCounter";
import { SelectVariationAttribute } from "../../fsd/features/SelectVatiationAttribute/SelectVatiationAttribute";
import { getLowestPriceVariation } from "../../fsd/shared/utils";

const cx = classNames.bind(styles);

export type ProductDetailParams = {
  id: string;
};

const ProductDetailFortime = () => {
  const { t } = useTranslation();
  const config = useConfig();
  const history = useHistory();
  const { search } = useLocation();
  const { state } = useContext(AppContext);
  const [showLoading, setShowLoading] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const ionContentRef = useRef<HTMLIonContentElement>(null);
  const { isUserAuthenticated } = useAuth();
  const { id } = useParams<ProductDetailParams>();

  const { activeShippingZone } = useActiveOrMainShippingZone();
  const { cartProductsMap, addToCart: addToCartStore, removeFromCart, setProductQuantity } = useCartData();
  const [selectedVariation, setSelectedVariation] = useState<TProduct | null>(null);

  const renderItem = (item: TProduct) => {
    return <FortimeMainPageProduct product={{ ...item, parentName: item?.name ?? "" }} lazyload={false} />;
  };

  useEffect(() => {
    ionContentRef.current!?.scrollToTop(500);
    setSelectedVariation(null);
  }, [id]);

  const {
    data: product,
    isLoading: isSingleProductLoading,
    isFetching: isSingleProductFetching,
    error: productError,
  } = useGetProductByIdQuery(id, activeShippingZone.id, {
    options: {
      enabled: !!activeShippingZone.id,
    },
  });

  const { data: category, isLoading: isCategoryLoading } = useGetCategoryByIdQuery(product?.categories?.[0]?.id ?? "", {
    enabled: !!product?.categories?.length,
  });

  const { data: categoryProductsResults, isFetching: isCategoryProductsLoading } = useGetProductsQuery(
    {
      zoneId: activeShippingZone.id,
      categoryId: category?.id,
    },
    {
      enabled: !!category?.id && !!activeShippingZone.id,
    },
  );

  const categoryProducts = categoryProductsResults?.products;

  const { data: variations, isFetching: isVariationsFetching } = useGetProductVariations(
    product?.variations ?? [],
    activeShippingZone.id,
    {
      enabled: !!product?.variations?.length && !!activeShippingZone.id,
    },
  );

  useEffect(() => {
    if (
      variations &&
      variations.length > 0 &&
      !isVariationsFetching &&
      !selectedVariation &&
      product?.type === "variable"
    ) {
      const lowestPriceVariation = getLowestPriceVariation(variations);

      if (lowestPriceVariation) {
        setSelectedVariation(lowestPriceVariation);
      }
    }
  }, [variations, isVariationsFetching, product?.type, selectedVariation]);

  const productForCart = product?.type === "variable" ? selectedVariation : product;

  const productId = productForCart?.id as string;
  const isInStock = productForCart?.stockStatus === "instock";

  // Get cart quantity for this product
  const cartQuantity = cartProductsMap?.[productId as string]?.quantity || 0;

  const getBasePrice = () => {
    if (product?.type === "variable" && selectedVariation) {
      const price = selectedVariation.price || selectedVariation.regularPrice || 0;
      return Number(price) || 0;
    }
    const price = product?.price || product?.regularPrice || 0;
    return Number(price) || 0;
  };
  const counterHook = useProductDetailCounter({
    product,
    selectedVariation,
    basePrice: getBasePrice(),
    cartQuantity,
  });

  function addToCart(newProduct: TProduct) {
    if (!newProduct?.purchasable) {
      return;
    }

    const productItem = newProduct.type === ProductType.VARIABLE ? selectedVariation! : newProduct;
    const unitName = (newProduct as any).unitName || counterHook.selectedAttribute?.name || "";
    const selectedAttribute = counterHook.selectedAttribute
      ? {
          id: counterHook.selectedAttribute.id,
          name: counterHook.selectedAttribute.name,
          coefficient: counterHook.selectedAttribute.coefficient,
        }
      : null;

    const quantity = counterHook.getCurrentQuantity();
    const parentName = newProduct.type === ProductType.VARIATION ? product?.name : "";

    for (let i = 0; i < quantity; i++) {
      addToCartStore({ ...productItem, unitName, selectedAttribute, parentName } as AddToCartProduct);
    }
  }

  function handleRemoveProductFromCart(id: ProductId) {
    removeFromCart(id, isUserAuthenticated);
  }

  function handleSetProductQuantity(productId: string, quantity: number, productData: TProduct) {
    const unitName = (productData as any).unitName || counterHook.selectedAttribute?.name || "";
    const selectedAttribute = counterHook.selectedAttribute
      ? {
          id: counterHook.selectedAttribute.id,
          name: counterHook.selectedAttribute.name,
          coefficient: counterHook.selectedAttribute.coefficient,
        }
      : null;

    const parentName = productData.type === ProductType.VARIATION ? product?.name : "";

    setProductQuantity(
      productId,
      quantity,
      { ...productData, unitName, selectedAttribute, parentName } as any,
      isUserAuthenticated,
    );
  }

  function checkProductNew() {
    if (product && product.dateCreated) {
      const pDate = new Date(product.dateCreated);
      const date = pDate.getTime() + state.config.newProductDuration * 86400000;
      const todayDate = new Date().getTime();

      return date > todayDate;
    }
  }

  const openProducts = (value: string) => {
    history.push(`/${value}`);
  };

  const handleImageLoad = (isImageLoaded: boolean) => {
    if (isImageLoaded) {
      setImagesLoaded(true);
    }
  };

  const handleVariationSelect = (variation: TProduct | null) => {
    setSelectedVariation(variation);
  };

  const handleAddToCart = (productToAdd: TProduct, quantity: number, type: "unit" | "package", attribute?: any) => {
    addToCart(productToAdd);
  };

  const headerBreadcrumbs =
    !isSingleProductLoading || !isCategoryLoading || !isCategoryProductsLoading || !isSingleProductFetching
      ? [
          {
            title: category ? category.name : t("Catalog"),
            handler: () => {
              return category
                ? openProducts(`products/${category.id}/${category.name}/popular`)
                : openProducts("search-main-categories");
            },
          },
          { title: product?.name ?? "" },
        ]
      : [];

  const goHome = () => {
    history.push("/home3");
  };

  const productNotFound =
    !isSingleProductLoading && !isSingleProductFetching && (productError as AxiosError)?.response?.status === 404;

  const hasMultipleImages = !isSingleProductLoading && !isSingleProductFetching && product?.images.length! > 1;

  const hasSwiperNavigation = hasMultipleImages && imagesLoaded;

  const swiperNavigation = hasSwiperNavigation
    ? {
        enabled: true,
      }
    : {
        enabled: false,
        prevEl: null,
        nextEl: null,
      };

  return (
    <Page>
      <Page.HeaderMobile showBackButton />

      <Page.Content ref={ionContentRef}>
        <Page.HeaderDesktop breadcrumbs={headerBreadcrumbs} />
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          message={t("Please wait...")}
          duration={20000}
        />
        <IonLoading
          isOpen={isSingleProductLoading || isSingleProductFetching}
          message={t("Please wait...")}
          duration={20000}
        />
        <div
          className={`${cx(
            "productDetailWrapper",
            "product-detail-page",
            "productDetailPage",
            productNotFound && "flex",
          )} global-content`}
        >
          <MaxWidthContainer
            smContainer={true}
            mdContainer={true}
            lgContainer={true}
            classList={cx("productDetailContainer", productNotFound && "flex-1")}
          >
            {!!product ? (
              <div>
                <IonRow className={cx("productDetailTop")}>
                  <IonCol sizeMd={"5"} className={cx("productDetailSliderWrapper")}>
                    <Swiper
                      spaceBetween={4}
                      slidesPerView={1}
                      modules={[Navigation]}
                      className={cx("productDetailSlider")}
                      navigation={swiperNavigation}
                    >
                      {!!product.videoUrl?.length && (
                        <SwiperSlide className={cx("productDetailSliderItem")}>
                          <video height={"100%"} className={cx("productDetailSlider__video")} controls>
                            <source src={product.videoUrl} type="video/mp4" />
                          </video>
                        </SwiperSlide>
                      )}
                      {product.images?.length === 0 ? (
                        <SwiperSlide className={cx("productDetailSliderItem")}>
                          <ProductDetailSlideBadges
                            product={product}
                            selectedVariation={selectedVariation}
                            isNewProduct={checkProductNew()!}
                          />
                          <div className={cx("slideImgWrapper")}>
                            <SmartImage image={undefined} className={cx("slideImg")} />
                          </div>
                        </SwiperSlide>
                      ) : (
                        product.images?.map((img) => (
                          <SwiperSlide key={img.id}>
                            <ProductDetailSlideBadges
                              product={product}
                              selectedVariation={selectedVariation}
                              isNewProduct={checkProductNew()!}
                            />
                            <div className={cx("slideImgWrapper")}>
                              <SmartImage image={img} isImageLoadedFn={handleImageLoad} className={cx("slideImg")} />
                            </div>
                          </SwiperSlide>
                        ))
                      )}
                    </Swiper>
                  </IonCol>
                  <IonCol sizeMd={"7"} className={cx("ion-hide-md-down")}>
                    <IonRow className={cx("productDetailTitleRow")}>
                      <IonCol className="productDetailTitleCol" size="12">
                        <h3 className={cx("productDetailTitle")}>{product?.name}</h3>
                      </IonCol>
                    </IonRow>
                    <IonRow>
                      <IonCol size="6">
                        <ProductDetailInStock
                          portionUnit={product.portionUnit ?? ""}
                          stockQuantity={product.stockQuantity}
                          label={!product?.purchasable ? t("Purchase is limited in time") : ""}
                          isInStock={product.stockStatus === "instock"}
                        />
                      </IonCol>
                      <IonCol size="6" className={cx("averageRatingWrapper")}>
                        {product.averageRating && (
                          <ProductDetailRating
                            rating={
                              product.averageRating ? product.averageRating?.slice(0, 3).replace(".", ",") : undefined
                            }
                          />
                        )}
                      </IonCol>
                    </IonRow>
                    {isVariationsFetching && (
                      <IonGrid>
                        <IonCol>
                          <IonSpinner />
                        </IonCol>
                      </IonGrid>
                    )}
                    {variations && product.type === ProductType.VARIABLE && (
                      <SelectVariationAttribute
                        variations={variations as TProduct[]}
                        onVariationSelect={handleVariationSelect}
                      />
                    )}
                    {product.type === ProductType.VARIABLE && (
                      <VariationBadgesFortime
                        variations={variations}
                        onVariationSelect={handleVariationSelect}
                        product={product}
                        selectedVariation={selectedVariation}
                        isNewProduct={checkProductNew()!}
                        counterHook={counterHook}
                        onAddToCart={addToCart}
                        onRemoveFromCart={handleRemoveProductFromCart}
                        onSetProductQuantity={handleSetProductQuantity}
                        cartQuantity={cartQuantity}
                      />
                    )}
                  </IonCol>
                </IonRow>

                <IonGrid className={cx("ion-hide-md-up", "productDetailHeadInfo")}>
                  <IonRow>
                    <IonCol size="6">
                      <ProductDetailInStock
                        stockQuantity={product.stockQuantity}
                        portionUnit={product.portionUnit ?? ""}
                        label={!product?.purchasable ? t("Purchase is limited in time") : ""}
                        isInStock={product.stockStatus === "instock"}
                      />
                    </IonCol>
                    <IonCol size="6" className={cx("averageRatingWrapper")}>
                      {product.averageRating && (
                        <ProductDetailRating
                          rating={
                            product.averageRating ? product.averageRating?.slice(0, 3).replace(".", ",") : undefined
                          }
                        />
                      )}
                    </IonCol>
                  </IonRow>
                  <IonRow>
                    <IonCol size="12">
                      <h3 className={cx("productDetailItemTitle")}>{product?.name}</h3>
                    </IonCol>
                  </IonRow>
                </IonGrid>
                {isVariationsFetching && (
                  <IonGrid className={cx("ion-hide-md-up")}>
                    <IonCol>
                      <IonSpinner />
                    </IonCol>
                  </IonGrid>
                )}
                {product.type === ProductType.VARIABLE && !isVariationsFetching && (
                  <IonGrid className={cx("productDetailAttributesGrid", "ion-hide-md-up")}>
                    <IonList>
                      {variations && product.type === ProductType.VARIABLE && !isVariationsFetching && (
                        <SelectVariationAttribute
                          variations={variations as TProduct[]}
                          onVariationSelect={handleVariationSelect}
                        />
                      )}
                    </IonList>
                  </IonGrid>
                )}

                <IonGrid className={cx("ion-hide-md-up")}>
                  <IonRow>
                    <IonCol>
                      {product.type === ProductType.VARIABLE && (
                        <VariationBadgesFortime
                          variations={variations}
                          onVariationSelect={handleVariationSelect}
                          product={product}
                          selectedVariation={selectedVariation}
                          isNewProduct={checkProductNew()!}
                          counterHook={counterHook}
                          cartQuantity={cartQuantity}
                          onAddToCart={addToCart}
                          onRemoveFromCart={handleRemoveProductFromCart}
                          onSetProductQuantity={handleSetProductQuantity}
                        />
                      )}
                    </IonCol>
                  </IonRow>
                </IonGrid>
                {config?.showProductReviews ? (
                  <div className={cx("productDetailInfo")}>
                    <ProductDetailInfo id={id} product={product} />
                  </div>
                ) : (
                  <IonGrid>
                    <IonRow>
                      <IonCol>
                        <h3 className={"ion-no-margin ion-margin-bottom"}>{t("Product Description")}</h3>
                        <ProductDescription props={product} />
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                )}
                <IonGrid class={cx("sameCategorySlider", "productDetailContent animate-product ion-no-padding")}>
                  {isCategoryProductsLoading && (
                    <IonCol>
                      <IonSpinner className="ion-margin-start" />
                    </IonCol>
                  )}
                  {categoryProducts && categoryProducts?.length > 0 && !isCategoryProductsLoading && (
                    <IonRow class="ion-no-padding">
                      <IonCol class="ion-no-padding" size="12">
                        <IonRow class="top-icon-header heading">
                          <p className={cx("relatedItems")}>{t("Products of this category")}</p>
                        </IonRow>
                        <div className={cx("sameCategorySliderWrapper")}>
                          <ProductsSlider items={categoryProducts} renderItem={renderItem} />
                        </div>
                      </IonCol>
                    </IonRow>
                  )}
                </IonGrid>
              </div>
            ) : productNotFound ? (
              <div className={cx("h-full flex justify-center items-center flex-col")}>
                <div>{t("No item found")}</div>
                <IonButton onClick={goHome}>{t("Go to home")}</IonButton>
              </div>
            ) : null}
          </MaxWidthContainer>
        </div>
        <Page.ContentFooter />
      </Page.Content>
      <Page.PageFooter>
        {product && productId && (
          <div className={cx("ion-hide-md-up")}>
            {product.type !== ProductType.VARIABLE && (
              <ProductDetailCounter
                isInStock={isInStock}
                isEmpty={!cartProductsMap[productId]?.quantity}
                product={product}
                selectedVariation={selectedVariation}
                cartQuantity={cartProductsMap[productId]?.quantity ?? 0}
                onAddToCart={addToCart}
                onRemoveFromCart={handleRemoveProductFromCart}
              />
            )}
          </div>
        )}
      </Page.PageFooter>
    </Page>
  );
};

export default withRouter(ProductDetailFortime);
